import { useState, useEffect, useCallback, useMemo, useRef } from "react"
import { Editor, EditorState, RichUtils, convertToRaw } from "draft-js"
import { FormatBold, FormatItalic, FormatListBulleted, FormatListNumbered, Undo, Redo } from "@mui/icons-material"
import { Snackbar, Button, Typography, Alert as MuiAlert } from "@mui/material"
import { convertToHTML, enhancedConvertFromHTML } from "./DraftToHtml"
import "draft-js/dist/Draft.css"
import styles from "./RichTextEditor.module.css"

// Custom debounce function implementation
function useDebounce(fn, delay) {
  const timeoutRef = useRef(null)

  const debouncedFn = useCallback(
    (...args) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      timeoutRef.current = setTimeout(() => {
        fn(...args)
      }, delay)
    },
    [fn, delay],
  )

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return debouncedFn
}

const BLOCK_TYPES = {
  h1: "header-one", // Retained for compatibility with existing content
  h2: "header-two",
  ul: "unordered-list-item",
  ol: "ordered-list-item",
}

export function RichTextEditor({
  onSave,
  disabled = false,
  initialContent = "",
  error = false,
  setUnsaved,
  parentEditMode = false,
  item,
  subsection,
  setUnsavedSubsections,
}) {
  // Add a key to force re-creation of the editor when initialContent changes
  const [editorKey, setEditorKey] = useState(0)

  const [editorState, setEditorState] = useState(() => {
    if (initialContent) {
      return EditorState.createWithContent(enhancedConvertFromHTML(initialContent))
    }
    return EditorState.createEmpty()
  })

  const [showSnackbar, setShowSnackbar] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  // Track the last saved content to detect real changes
  const lastSavedContentRef = useRef(initialContent)

  // This effect runs when initialContent changes (like when canceling)
  // useEffect(() => {
  //   // If the content has changed from what we last saved
  //   if (initialContent !== lastSavedContentRef.current) {
  //     console.log("PAPAPPAPAPPAPAPAPPAPAPAPPAPAPPAPAPAP")
  //     // Reset the editor with the new content
  //     setEditorState(EditorState.createWithContent(enhancedConvertFromHTML(initialContent)))
  //     // Reset unsaved changes flag
  //     setHasUnsavedChanges(false)
  //     // Update our reference to the latest content
  //     lastSavedContentRef.current = initialContent
  //     // Increment the key to force a complete re-render of the editor
  //     setEditorKey((prev) => prev + 1)

  //     // Also clear any unsaved subsections tracking for this item
  //     if (item?.id && subsection?.id) {
  //       setUnsavedSubsections((prev) => {
  //         const newState = { ...prev }
  //         delete newState[`${item.id}-${subsection.id}`]
  //         return newState
  //       })
  //     }
  //   }
  // }, [initialContent, item?.id, subsection?.id, setUnsavedSubsections])

  // Function to mark content as unsaved
  const markUnsaved = useCallback(() => {
    setHasUnsavedChanges(true)
    if (item?.id && subsection?.id) {
      setUnsavedSubsections((prev) => ({
        ...prev,
        [`${item.id}-${subsection.id}`]: true,
      }))
    }
  }, [item?.id, subsection?.id, setUnsavedSubsections])

  // Debounced version of markUnsaved
  const debouncedMarkUnsaved = useDebounce(markUnsaved, 500)

  const handleKeyCommand = (command) => {
    const newState = RichUtils.handleKeyCommand(editorState, command)
    if (newState) {
      setEditorState(newState)
      debouncedMarkUnsaved()
      return "handled"
    }
    return "not-handled"
  }

  const toggleInlineStyle = useCallback(
    (style) => {
      if (disabled) return
      setEditorState(RichUtils.toggleInlineStyle(editorState, style))
      markUnsaved()
    },
    [disabled, editorState, markUnsaved],
  )

  const toggleBlockType = useCallback(
    (blockType) => {
      if (disabled) return
      setEditorState(RichUtils.toggleBlockType(editorState, blockType))
      markUnsaved()
    },
    [disabled, editorState, markUnsaved],
  )

  const undo = useCallback(() => {
    if (disabled) return
    setEditorState(EditorState.undo(editorState))
    markUnsaved()
  }, [disabled, editorState, markUnsaved])

  const redo = useCallback(() => {
    if (disabled) return
    setEditorState(EditorState.redo(editorState))
    markUnsaved()
  }, [disabled, editorState, markUnsaved])

  const getBlockStyle = (block) => {
    const type = block.getType()
    switch (type) {
      case BLOCK_TYPES.h1:
        return styles.h1 // Retained for existing h1 content
      case BLOCK_TYPES.h2:
        return styles.h2
      default:
        return ""
    }
  }

  const saveContent = useCallback(() => {
    const content = editorState.getCurrentContent()
    const rawContent = convertToRaw(content)
    const htmlContent = convertToHTML(rawContent)

    // Save locally and mark as saved in the parent component
    onSave?.(htmlContent)
    setShowSnackbar(true)

    // Update our reference to the latest saved content
    lastSavedContentRef.current = htmlContent

    // We're no longer showing unsaved changes in the editor UI
    setHasUnsavedChanges(false)

    // Mark as saved in the parent component too
    if (item?.id && subsection?.id) {
      setUnsavedSubsections((prev) => {
        const newState = { ...prev }
        delete newState[`${item.id}-${subsection.id}`]
        return newState
      })
    }
  }, [editorState, item?.id, onSave, subsection?.id, setUnsavedSubsections])

  // Optimized handleEditorChange - only updates editor state immediately
  // and debounces the "unsaved changes" updates
  const handleEditorChange = useCallback(
    (newState) => {
      const contentChanged = newState.getCurrentContent() !== editorState.getCurrentContent()

      // Always update the editor state immediately for responsive typing
      setEditorState(newState)

      // Only trigger the unsaved state updates if content actually changed
      if (contentChanged) {
        debouncedMarkUnsaved()
      }
    },
    [editorState, debouncedMarkUnsaved],
  )

  // Memoize these values to prevent unnecessary re-renders
  const currentStyle = useMemo(() => editorState.getCurrentInlineStyle(), [editorState])
  const currentBlockType = useMemo(
    () => editorState.getCurrentContent().getBlockForKey(editorState.getSelection().getStartKey()).getType(),
    [editorState],
  )

  const isReadOnly = disabled

  return (
    <>
      <div className={`${styles.editorContainer} ${error ? styles.errorContainer : ""}`} key={editorKey}>
        <div className={styles.toolbar}>
          <button
            className={`${styles.button} ${currentStyle.has("BOLD") ? styles.active : ""}`}
            onClick={() => toggleInlineStyle("BOLD")}
            title="Bold"
            disabled={isReadOnly}
          >
            <FormatBold />
          </button>
          <button
            className={`${styles.button} ${currentStyle.has("ITALIC") ? styles.active : ""}`}
            onClick={() => toggleInlineStyle("ITALIC")}
            title="Italic"
            disabled={isReadOnly}
          >
            <FormatItalic />
          </button>
          <button
            className={`${styles.button} ${currentBlockType === BLOCK_TYPES.ul ? styles.active : ""}`}
            onClick={() => toggleBlockType(BLOCK_TYPES.ul)}
            title="Bullet List"
            disabled={isReadOnly}
          >
            <FormatListBulleted />
          </button>
          <button
            className={`${styles.button} ${currentBlockType === BLOCK_TYPES.ol ? styles.active : ""}`}
            onClick={() => toggleBlockType(BLOCK_TYPES.ol)}
            title="Numbered List"
            disabled={isReadOnly}
          >
            <FormatListNumbered />
          </button>
          <div className={styles.divider} />
          <button
            className={styles.button}
            onClick={undo}
            title="Undo"
            disabled={isReadOnly || !editorState.getUndoStack().size}
          >
            <Undo />
          </button>
          <button
            className={styles.button}
            onClick={redo}
            title="Redo"
            disabled={isReadOnly || !editorState.getRedoStack().size}
          >
            <Redo />
          </button>
          <Button
            variant="contained"
            onClick={saveContent}
            disabled={isReadOnly}
            color={hasUnsavedChanges ? "warning" : "primary"}
            style={{ marginLeft: "8px" }}
          >
            {hasUnsavedChanges ? "Save*" : "Save"}
          </Button>
        </div>

        <div className={styles.editorContent}>
          {hasUnsavedChanges && (
            <Typography variant="caption" color="warning.main" sx={{ display: "block", mb: 1 }}>
              * You have unsaved changes
            </Typography>
          )}
          <Editor
            editorState={editorState}
            onChange={handleEditorChange}
            handleKeyCommand={handleKeyCommand}
            blockStyleFn={getBlockStyle}
            placeholder={
              editorState.getCurrentContent().getBlockMap().first().getType() === "unstyled" &&
              !editorState.getCurrentContent().hasText()
                ? "Start typing..."
                : ""
            }
            readOnly={isReadOnly}
          />
        </div>

        <Snackbar
          open={showSnackbar}
          autoHideDuration={2000}
          onClose={() => setShowSnackbar(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <MuiAlert severity="success" onClose={() => setShowSnackbar(false)}>
            Content saved successfully
          </MuiAlert>
        </Snackbar>
      </div>
    </>
  )
}

